/// <amd-module name="VSS/Features/MarkdownToolbar/TableGrid" />
import * as React from "react";
import { format } from "VSS/Core/Util/String";
import { css, KeyCode } from "VSSUI/Util";

import * as Resources from "VSS/Features/MarkdownToolbar/Resources";

export interface ITableGridProps {
    onActivated: (tableSize: TableSize) => void;
}

interface ITableGridState {
    selectedTableSize: TableSize;
}

export interface TableSize {
    rowCount: number;
    colCount: number;
}

const nrows = 15;
const ncols = 15;
const initialRows = 2;
const initialCols = 2;

export class TableGrid extends React.Component<ITableGridProps, ITableGridState> {
    private _tableRef = React.createRef<HTMLTableElement>();

    public constructor(props: ITableGridProps) {
        super(props);

        this.state = { selectedTableSize: { rowCount: initialRows, colCount: initialCols } };
    }

    public render(): JSX.Element {
        const rows: JSX.Element[] = [];
        for (let i = 0; i < nrows; i++) {
            const cols: JSX.Element[] = [];
            for (let j = 0; j < ncols; j++) {
                cols.push(this._getTableCell(i, j));
            }
            rows.push(<tr key={"row-" + i}>{cols}</tr>);
        }
        const { selectedTableSize } = this.state;

        return (
            <>
                <div className="selected-table-info body-m font-weight-semibold">
                    {format(Resources.SelectedTableInfo, selectedTableSize.colCount, selectedTableSize.rowCount)}
                </div>
                <table
                    className={"insert-table-grid"}
                    aria-colcount={ncols}
                    aria-rowcount={nrows}
                    role="grid"
                    onMouseLeave={this._onMouseLeave}
                    ref={this._tableRef}
                >
                    <tbody>{rows}</tbody>
                </table>
            </>
        );
    }

    public componentDidMount(): void {
        const { rowCount, colCount } = this.state.selectedTableSize;
        this._focus(rowCount - 1, colCount - 1);
    }

    private _getTableCell = (rowIndex: number, columnIndex: number): JSX.Element => {
        const { onActivated } = this.props;
        const { selectedTableSize } = this.state;

        return (
            <td key={"col-" + columnIndex}>
                <div
                    aria-label={format(Resources.TableCellAriaLabel, rowIndex + 1, columnIndex + 1)}
                    data-is-focusable={true}
                    className={css(
                        this._getDefaultCellClassName(rowIndex, columnIndex),
                        columnIndex < selectedTableSize.colCount && rowIndex < selectedTableSize.rowCount && "higlighted-cell"
                    )}
                    onClick={() => {
                        onActivated({ rowCount: rowIndex + 1, colCount: columnIndex + 1 });
                    }}
                    onMouseOver={() => {
                        this._focus(rowIndex, columnIndex);
                    }}
                    onFocus={() => {
                        this._onFocus(rowIndex, columnIndex);
                    }}
                    onKeyDown={ev => {
                        this._onKeyDown(ev, rowIndex, columnIndex);
                    }}
                />
            </td>
        );
    };

    private _onFocus = (rowIndex: number, colIndex: number): void => {
        const { rowCount, colCount } = this.state.selectedTableSize;
        // don't select already selected cell.
        if (rowIndex + 1 !== rowCount || colIndex + 1 !== colCount) {
            this._onSelected({ rowCount: rowIndex + 1, colCount: colIndex + 1 });
        }
    };

    private _onSelected = (selectedTableSize: TableSize): void => {
        this.setState({ selectedTableSize });
    };

    private _onMouseLeave = (): void => {
        this._focus(initialRows - 1, initialCols - 1); // as it's index, subtract 1.
    };

    private _onKeyDown = (ev: React.KeyboardEvent<HTMLDivElement>, rowIndex: number, colIndex: number): void => {
        let nextRowIndex = rowIndex;
        let nextColIndex = colIndex;

        if (ev.which == KeyCode.downArrow) {
            nextRowIndex = (rowIndex + 1) % nrows;
        } else if (ev.which == KeyCode.upArrow) {
            // according to javascript, -1 % 3 = -1 and what we want is 2. so we're doing (-1 + 3) % 3
            nextRowIndex = (rowIndex + nrows - 1) % nrows;
        } else if (ev.which == KeyCode.leftArrow) {
            nextColIndex = (colIndex + ncols - 1) % ncols;
        } else if (ev.which == KeyCode.rightArrow) {
            nextColIndex = (colIndex + 1) % ncols;
        }

        if (nextRowIndex !== rowIndex || nextColIndex !== colIndex) {
            ev.stopPropagation();
            this._focus(nextRowIndex, nextColIndex);
        }
    };

    private _focus = (rowIndex: number, colIndex: number): void => {
        const tableElement = this._tableRef.current;
        if (tableElement) {
            const { rowCount, colCount } = this.state.selectedTableSize;
            const currentCell = tableElement.getElementsByClassName(this._getDefaultCellClassName(rowCount - 1, colCount - 1))[0] as HTMLElement;
            // update current cell's tabIndex so that it is not tabbable.
            currentCell.setAttribute("tabIndex", "-1");

            const selectedCell = tableElement.getElementsByClassName(this._getDefaultCellClassName(rowIndex, colIndex))[0] as HTMLElement;
            selectedCell.setAttribute("tabIndex", "0");
            selectedCell.focus();
        }
    };

    private _getDefaultCellClassName(rowIndex: number, colIndex: number): string {
        return css("md-insert-table-cell", "row-" + rowIndex, "col-" + colIndex);
    }
}
